import { z } from 'zod'

export const INTERVIEW_STAGE_OPTIONS = [
  {
    value: 'recruiter-call',
    label: 'Recruiter Call',
  },
  {
    value: 'online-assessment',
    label: 'Online Assessment',
  },
  {
    value: 'phone-screen',
    label: 'Phone Screen',
  },
  {
    value: 'onsite',
    label: 'Onsite',
    note: 'Onsites often span more than a single day.',
  },
  {
    value: 'team-match',
    label: 'Team Match',
  },
  {
    value: 'offer-call',
    label: 'Offer Call',
  },
  {
    value: 'custom',
    label: 'Custom Round',
  },
] as const

export const INTERVIEW_STAGES = INTERVIEW_STAGE_OPTIONS.map(
  (stage) => stage.value,
)

export type InterviewStage = string

export const STAGE_META: Record<string, { label: string; note?: string }> =
  Object.fromEntries(
    INTERVIEW_STAGE_OPTIONS.map((stage) => [
      stage.value,
      { label: stage.label, note: 'note' in stage ? stage.note : undefined },
    ]),
  )

export const INTERVIEW_FOCUS_OPTIONS = [
  { value: 'dsa', label: 'DS&A' },
  { value: 'ai-coding', label: 'AI Coding' },
  { value: 'low-level-design', label: 'Low-level Design' },
  { value: 'code-review', label: 'Code Review' },
  { value: 'system-design', label: 'System Design' },
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'domain', label: 'Domain' },
  { value: 'hiring-assessment', label: 'Hiring Assessment' },
] as const

export const INTERVIEW_FOCUSES = INTERVIEW_FOCUS_OPTIONS.map(
  (focus) => focus.value,
)

export type InterviewFocus = string

export const FOCUS_META: Record<string, { label: string }> = Object.fromEntries(
  INTERVIEW_FOCUS_OPTIONS.map((focus) => [focus.value, { label: focus.label }]),
)

export const INTERVIEW_COMPANY_DIRECTORY = [
  'Google',
  'Meta',
  'Apple',
  'Amazon',
  'Netflix',
  'OpenAI',
  'Anthropic',
  'Microsoft',
  'Nvidia',
  'Tesla',
  'Stripe',
  'Airbnb',
  'Uber',
  'Lyft',
  'DoorDash',
  'Instacart',
  'Coinbase',
  'Robinhood',
  'Plaid',
  'Square',
  'Shopify',
  'Snowflake',
  'Databricks',
  'Datadog',
  'MongoDB',
  'Cloudflare',
  'Vercel',
  'Figma',
  'Notion',
  'Linear',
  'Ramp',
  'Brex',
  'Scale AI',
  'Perplexity',
  'Mistral AI',
  'Cohere',
  'Hugging Face',
  'Palantir',
  'Salesforce',
  'Adobe',
  'Intuit',
  'PayPal',
  'Block',
  'Pinterest',
  'Reddit',
  'Discord',
  'Twitch',
  'Roblox',
  'Atlassian',
  'GitLab',
  'GitHub',
  'Dropbox',
  'Box',
  'Slack',
  'Zoom',
  'Okta',
  'Twilio',
  'SpaceX',
  'Waymo',
  'Cruise',
  'Rivian',
  'Samsara',
  'Two Sigma',
  'Jane Street',
  'Citadel',
  'Hudson River Trading',
  'Jump Trading',
  'Bloomberg',
  'Goldman Sachs',
  'JPMorgan Chase',
  'Capital One',
  'American Express',
  'Visa',
  'Mastercard',
  'Oracle',
  'IBM',
  'Intel',
  'AMD',
  'Qualcomm',
  'Broadcom',
  'ByteDance',
  'TikTok',
  'Spotify',
  'Duolingo',
  'Chime',
  'Affirm',
  'Klarna',
  'Wise',
  'Etsy',
  'eBay',
  'Zillow',
  'Redfin',
  'Booking.com',
  'Expedia',
] as const

export interface InterviewContact {
  id: string
  name: string
  role: string
  email: string
  phone: string
}

export interface InterviewLink {
  id: string
  label: string
  url: string
}

export interface InterviewNote {
  id: string
  body: string
  createdAt: string
  updatedAt: string
}

export type InterviewScheduling =
  | { kind: 'locked'; startDate: string; endDate: string }
  | { kind: 'availability'; dates: string[] }
  | { kind: 'tbd' }

export interface ScheduledInterview {
  id: string
  applicationId?: string
  applicationStageId?: string
  company: string
  roleTitle?: string
  stage: string
  stageLabel?: string
  focuses: string[]
  scheduling: InterviewScheduling
  notes: string
  contacts: InterviewContact[]
  links: InterviewLink[]
  noteEntries: InterviewNote[]
}

const dateKeySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`)
    if (Number.isNaN(parsed.getTime())) return false
    const year = parsed.getUTCFullYear()
    const month = String(parsed.getUTCMonth() + 1).padStart(2, '0')
    const day = String(parsed.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}` === value
  }, 'Expected a real calendar date in YYYY-MM-DD')

const labelSchema = z.string().trim().min(1).max(160)
const optionalLabelSchema = z.string().trim().max(160).default('')

export const interviewStageSchema = labelSchema
export const interviewFocusSchema = labelSchema

export const interviewContactInputSchema = z.object({
  id: z.string().optional(),
  name: z.string().trim().min(1).max(160),
  role: optionalLabelSchema,
  email: z.string().trim().max(254).default(''),
  phone: z.string().trim().max(64).default(''),
})

export const interviewLinkInputSchema = z.object({
  id: z.string().optional(),
  label: optionalLabelSchema,
  url: z.string().trim().url().max(2048),
})

export const interviewSchedulingInputSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('locked'),
      startDate: dateKeySchema,
      endDate: dateKeySchema,
    })
    .refine((value) => value.endDate >= value.startDate, {
      message: 'End date must be on or after start date',
      path: ['endDate'],
    }),
  z.object({
    kind: z.literal('availability'),
    dates: z
      .array(dateKeySchema)
      .min(1)
      .max(40)
      .transform((dates) => [...new Set(dates)].sort()),
  }),
  // Interview confirmed but not scheduled yet — no dates to record.
  z.object({ kind: z.literal('tbd') }),
])

export const clientInterviewInputSchema = z.object({
  applicationId: z.string().uuid().optional(),
  applicationStageId: z.string().uuid().optional(),
  company: labelSchema,
  roleTitle: optionalLabelSchema,
  stage: interviewStageSchema,
  stageLabel: optionalLabelSchema.optional(),
  focuses: z
    .array(interviewFocusSchema)
    .max(20)
    .transform((focuses) => [...new Set(focuses)]),
  scheduling: interviewSchedulingInputSchema,
  notes: z.string().max(5000).default(''),
  contacts: z.array(interviewContactInputSchema).max(20).default([]),
  links: z.array(interviewLinkInputSchema).max(20).default([]),
})

export type ClientInterviewInput = z.infer<typeof clientInterviewInputSchema>

export function toClientInterviewInput(
  interview: ScheduledInterview,
): ClientInterviewInput {
  return {
    ...(interview.applicationId
      ? { applicationId: interview.applicationId }
      : {}),
    ...(interview.applicationStageId
      ? { applicationStageId: interview.applicationStageId }
      : {}),
    company: interview.company,
    roleTitle: interview.roleTitle?.trim() ?? '',
    stage: interview.stage,
    ...(interview.stageLabel?.trim()
      ? { stageLabel: interview.stageLabel.trim() }
      : {}),
    focuses: interview.focuses,
    scheduling: interview.scheduling,
    notes: interview.notes,
    contacts: interview.contacts.map((contact) => ({
      id: contact.id,
      name: contact.name,
      role: contact.role,
      email: contact.email,
      phone: contact.phone,
    })),
    links: interview.links.map((link) => ({
      id: link.id,
      label: link.label,
      url: link.url,
    })),
  }
}

export function stageLabelFor(stage: string, explicitLabel?: string): string {
  const trimmed = explicitLabel?.trim()
  if (trimmed) return trimmed
  return STAGE_META[stage]?.label ?? stage
}

export function focusLabelFor(focus: string): string {
  return FOCUS_META[focus]?.label ?? focus
}

function toUtcDateKey(date: Date): string {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function interviewDates(interview: ScheduledInterview): string[] {
  if (interview.scheduling.kind === 'tbd') return []
  if (interview.scheduling.kind === 'availability') {
    return [...interview.scheduling.dates].sort()
  }

  const out: string[] = []
  const end = new Date(`${interview.scheduling.endDate}T00:00:00Z`)
  for (
    let d = new Date(`${interview.scheduling.startDate}T00:00:00Z`);
    d <= end;
    d.setUTCDate(d.getUTCDate() + 1)
  ) {
    out.push(toUtcDateKey(d))
  }
  return out
}

export function fuzzySearchCompanies(query: string, limit = 8): string[] {
  const q = query.trim().toLowerCase()
  if (!q) return []

  const scored: { name: string; score: number }[] = []
  for (const name of INTERVIEW_COMPANY_DIRECTORY) {
    const lower = name.toLowerCase()
    if (lower.startsWith(q)) {
      scored.push({ name, score: 0 })
      continue
    }
    if (lower.includes(q)) {
      scored.push({ name, score: 1 })
      continue
    }
    let qi = 0
    for (let i = 0; i < lower.length && qi < q.length; i++) {
      if (lower[i] === q[qi]) qi++
    }
    if (qi === q.length) scored.push({ name, score: 2 })
  }

  return scored
    .sort((a, b) => a.score - b.score || a.name.localeCompare(b.name))
    .slice(0, limit)
    .map((sc) => sc.name)
}
